<template>
  <v-container fluid>
    <div class="d-block d-md-flex">
      <AppAside></AppAside>
      <div class="ps-5 page_content grow">
        <!-- Loading Skeleton -->
        <v-skeleton-loader v-if="!data_loaded" type="table"></v-skeleton-loader>

        <!-- Main Card -->
        <v-card
          v-if="data_loaded"
          class="gift-campaigns-card"
          elevation="4"
          rounded="xl"
        >
          <!-- Header -->
          <div class="card-header pa-6 pb-4">
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div class="d-flex align-center">
                <div class="header-icon-wrapper me-4">
                  <v-icon large color="white">mdi-gift</v-icon>
                </div>
                <div>
                  <h1 class="text-h5 font-weight-bold white--text mb-1">
                    מארזי מתנה
                  </h1>
                  <p class="text-caption white--text text--lighten-3 mb-0">
                    {{ items.length }} מארזי מתנה במערכת
                  </p>
                </div>
              </div>
              <v-btn
                v-if="$store.getters.permission('gift_campaigns create')"
                color="white"
                class="indigo--text text--darken-4 font-weight-bold"
                elevation="3"
                rounded
                to="/gift-campaign"
              >
                <v-icon start>mdi-plus</v-icon>
                מארז חדש
              </v-btn>
            </div>
          </div>

          <!-- Table -->
          <v-data-table
            :mobile-breakpoint="0"
            :hide-default-footer="true"
            :disable-pagination="true"
            :items="items"
            :headers="headers"
            class="gift-table"
            :class="{ 'empty-table': items.length === 0 }"
          >
            <!-- Gift Name with Image -->
            <template v-slot:[`item.gift_name`]="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar
                  size="40"
                  rounded
                  class="me-3 gift-thumb"
                  v-if="item.gift_image"
                >
                  <img
                    :src="`${$store.state.def_path}/${item.gift_image}`"
                    :alt="item.gift_name"
                  />
                </v-avatar>
                <v-avatar
                  size="40"
                  rounded
                  color="indigo lighten-5"
                  class="me-3"
                  v-else
                >
                  <v-icon color="indigo" small>mdi-gift</v-icon>
                </v-avatar>
                <span class="font-weight-medium">{{ item.gift_name }}</span>
              </div>
            </template>

            <!-- Type Chip -->
            <template v-slot:[`item.type`]="{ item }">
              <v-chip
                small
                :color="item.type === 'brand' ? 'purple lighten-1' : 'blue darken-1'"
                text-color="white"
                class="type-chip"
              >
                <v-icon start x-small class="me-1">
                  {{ item.type === 'brand' ? 'mdi-tag-multiple' : 'mdi-cart' }}
                </v-icon>
                {{ item.type === 'brand' ? 'לפי מותג' : 'סכום עגלה' }}
              </v-chip>
            </template>

            <!-- Brand -->
            <template v-slot:[`item.brand`]="{ item }">
              <div v-if="item.type === 'brand' && item.brand" class="d-flex align-center">
                <v-avatar size="28" rounded class="me-2" v-if="item.brand.image">
                  <img :src="`${$store.state.def_path}/${item.brand.image}`" />
                </v-avatar>
                <span class="text-body-2">{{ item.brand.name }}</span>
              </div>
              <span v-else class="grey--text text--lighten-1">—</span>
            </template>

            <!-- Value -->
            <template v-slot:[`item.value_to_get_gift`]="{ item }">
              <span class="font-weight-bold indigo--text text--darken-2">
                ₪{{ item.value_to_get_gift }}
              </span>
            </template>

            <!-- Times -->
            <template v-slot:[`item.times`]="{ item }">
              <v-chip small outlined color="indigo" class="times-chip">
                {{ item.times }}x
              </v-chip>
            </template>

            <!-- Status Pill -->
            <template v-slot:[`item.status`]="{ item }">
              <div class="d-flex align-center">
                <v-chip
                  small
                  :color="item.status == 1 ? 'success' : 'grey lighten-1'"
                  :text-color="item.status == 1 ? 'white' : 'white'"
                  class="status-pill"
                >
                  <v-icon start x-small class="me-1">
                    {{ item.status == 1 ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  {{ item.status == 1 ? 'פעיל' : 'לא פעיל' }}
                </v-chip>
              </div>
            </template>

            <!-- Actions -->
            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex align-center">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="$store.getters.permission('gift_campaigns edit')"
                      :to="`/gift-campaign/${item.id}`"
                      small
                      icon
                      class="action-btn edit-btn me-1"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>עריכה</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      icon
                      v-if="$store.getters.permission('gift_campaigns delete')"
                      @click="delete_row(item.id)"
                      class="action-btn delete-btn"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>מחיקה</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>

          <!-- Empty State -->
          <div v-if="items.length === 0" class="empty-state pa-8 text-center">
            <v-icon size="80" color="indigo lighten-4">mdi-gift-outline</v-icon>
            <h3 class="text-h6 mt-4 grey--text text--darken-1">אין מארזי מתנה עדיין</h3>
            <p class="text-body-2 grey--text text--lighten-1 mt-2">
              לחצו על "מארז חדש" כדי להוסיף מארז מתנה ראשון
            </p>
            <v-btn
              v-if="$store.getters.permission('gift_campaigns create')"
              color="indigo"
              dark
              rounded
              class="mt-4"
              to="/gift-campaign"
            >
              <v-icon start>mdi-plus</v-icon>
              הוספת מארז מתנה
            </v-btn>
          </div>
        </v-card>

        <!-- Delete Dialog -->
        <DeleteDialog
          v-if="$store.getters.permission('gift_campaigns delete')"
          @delete_done="get_data()"
          @close_delete="delete_obj.delete_prop = false"
          :delete_obj="delete_obj"
        ></DeleteDialog>
      </div>
    </div>
  </v-container>
</template>

<script>
import DeleteDialog from "@/components/public/delete.vue";

export default {
  name: "gift_campaigns",
  data() {
    return {
      data_loaded: false,
      items: [],
      delete_obj: {
        title: "מארז מתנה",
        delete_prop: false,
        delete_url: "gift-campaigns",
        id: 0,
      },
      headers: [
        { text: "שם מתנה", value: "gift_name" },
        { text: "סוג", value: "type" },
        { text: "מותג", value: "brand" },
        { text: "סכום מינימלי", value: "value_to_get_gift" },
        { text: "פעמים", value: "times" },
        { text: "תאריך התחלה", value: "start_date" },
        { text: "תאריך סיום", value: "end_date" },
        { text: "סטטוס", value: "status" },
        { text: "תאריך יצירה", value: "created_at" },
        { text: "פעולות", value: "actions", sortable: false },
      ],
    };
  },
  components: {
    DeleteDialog,
  },
  mounted() {
    this.get_data();
  },
  methods: {
    get_data() {
      this.$store
        .dispatch("public__request", {
          config: {
            url: "gift-campaigns",
            method: "get",
          },
        })
        .then((res) => {
          this.items = (res.data && res.data.data) || [];
          this.data_loaded = true;
        });
    },
    delete_row(id) {
      this.delete_obj.id = id;
      this.delete_obj.delete_prop = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.gift-campaigns-card {
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.card-header {
  background: linear-gradient(135deg, #3949ab 0%, #5c6bc0 50%, #7986cb 100%);
  border-radius: 0;
}

.gift-table {
  ::v-deep th {
    font-weight: 700 !important;
    color: #3949ab !important;
    font-size: 0.85rem;
    border-bottom: 2px solid #e8eaf6 !important;
    background: #f5f7ff;
  }

  ::v-deep td {
    vertical-align: middle !important;
    padding: 12px 16px !important;
  }

  ::v-deep tr:hover {
    background-color: #f5f7ff !important;
    transition: background-color 0.2s ease;
  }
}

.gift-thumb {
  border: 2px solid #e8eaf6;
  overflow: hidden;

  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
}

.type-chip {
  font-weight: 600;
  letter-spacing: 0.3px;
  border-radius: 20px;
}

.times-chip {
  border-radius: 16px;
  font-weight: 600;
}

.status-pill {
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.75rem;
}

.action-btn {
  transition: all 0.2s ease;

  &.edit-btn:hover {
    color: #3949ab !important;
  }

  &.delete-btn:hover {
    color: #e53935 !important;
  }
}

.empty-state {
  background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
  border-radius: 0 0 24px 24px;
}
</style>